import type { GridComponent } from '@syncfusion/ej2-react-grids';

/** View row indices (respect sort/filter); used for toolbar state, not for mutating `dataSource` order. */
export function getUniqueViewRowIndicesFromCellSelection(grid: GridComponent): number[] {
    const selected = grid.getSelectedRowCellIndexes?.() ?? [];
    const rowSet = new Set<number>();
    for (const block of selected) {
        if (block.cellIndexes != null && block.cellIndexes.length > 0) {
            rowSet.add(block.rowIndex);
        }
    }
    if (rowSet.size === 0 && grid.isEdit) {
        const editedCell = grid.element.querySelector('.e-editedbatchcell') as HTMLElement | null;
        if (editedCell) {
            const idxAttr = editedCell.getAttribute('index');
            const parsed = idxAttr != null ? parseInt(idxAttr, 10) : NaN;
            if (!Number.isNaN(parsed)) {
                rowSet.add(parsed);
            }
        }
    }
    return [...rowSet].sort((a, b) => a - b);
}

export function rowNumberFromGridRowData(rowData: unknown): number | null {
    if (!rowData || typeof rowData !== 'object') {
        return null;
    }
    const raw = (rowData as Record<string, unknown>).RowNo;
    if (raw === null || raw === undefined) {
        return null;
    }
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
}

/**
 * Resolves primary keys for the given view row indices so delete works after sort (view index ≠ dataSource order).
 * If mapping yields no RowNos but a cell is being batch-edited, falls back to the edited cell's row (same as legacy path).
 */
export function viewIndicesToRowNumbers(grid: GridComponent, viewIndices: number[]): number[] {
    const rowNoSet = new Set<number>();
    for (const viewIdx of viewIndices) {
        const rowEl = grid.getRowByIndex?.(viewIdx) as HTMLElement | null | undefined;
        if (!rowEl) {
            continue;
        }
        const cell = rowEl.querySelector<HTMLElement>('.e-rowcell');
        const info = grid.getRowInfo((cell ?? rowEl) as HTMLElement);
        const rowNo = rowNumberFromGridRowData(info?.rowData);
        if (rowNo != null) {
            rowNoSet.add(rowNo);
        }
    }
    if (rowNoSet.size === 0 && grid.isEdit) {
        const editedCell = grid.element.querySelector('.e-editedbatchcell') as HTMLElement | null;
        if (editedCell) {
            let rowNo = rowNumberFromGridRowData(grid.getRowInfo(editedCell)?.rowData);
            if (rowNo == null) {
                const rowEl = editedCell.closest('tr') as HTMLElement | null;
                if (rowEl) {
                    rowNo = rowNumberFromGridRowData(grid.getRowInfo(rowEl)?.rowData);
                }
            }
            if (rowNo != null) {
                rowNoSet.add(rowNo);
            }
        }
    }
    return [...rowNoSet].sort((a, b) => a - b);
}

/** Row numbers for all selected cells (or active batch-edit row), stable under sort/filter. */
export function getUniqueRowNosFromCellSelection(grid: GridComponent): number[] {
    return viewIndicesToRowNumbers(grid, getUniqueViewRowIndicesFromCellSelection(grid));
}
